import type { LegacyPublication } from "@/api/legacy-publications.service";
import type { NewAxisEdition } from "@/api/surveyTaxonomy.service";

export const FIRST_PUBLICATION_YEAR = 2017;

export function buildPublicationYearOptions(
  currentYear = new Date().getFullYear(),
): { value: string; label: string }[] {
  const years: { value: string; label: string }[] = [{ value: "", label: "Select year" }];
  for (let year = currentYear + 1; year >= FIRST_PUBLICATION_YEAR; year--) {
    years.push({ value: String(year), label: String(year) });
  }
  return years;
}

export function formatLegacyPublicationLabel(pub: LegacyPublication): string {
  const base = pub.publication || `Publication ${pub.id}`;
  return pub.edition ? `${base} / ${pub.edition}` : base;
}

export function formatLegacyPublicationOption(pub: LegacyPublication): {
  value: string;
  label: string;
} {
  return { value: String(pub.id), label: formatLegacyPublicationLabel(pub) };
}

/** Stored opt-out `edition` values are legacy_publication.id strings. */
export function isStoredLegacyPublicationId(value: string | null | undefined): boolean {
  return Boolean(value && /^\d+$/.test(value));
}

const EDITION_UUID = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;

export function isStoredEditionUuid(value: string | null | undefined): boolean {
  return Boolean(value && EDITION_UUID.test(value));
}

export function hasStoredEditionUuid(values: (string | null | undefined)[]): boolean {
  return values.some(isStoredEditionUuid);
}

export function lowercaseEditionOptions(
  options: { value: string; label: string }[],
): { value: string; label: string }[] {
  return options.map(option => ({ ...option, value: option.value.toLowerCase() }));
}

export function buildNewAxisEditionLookup(
  editions: NewAxisEdition[] | null | undefined,
): Map<string, NewAxisEdition> {
  return new Map((editions ?? []).map(edition => [edition.editionId.toLowerCase(), edition]));
}

export function extractPublicationYear(edition: string | null | undefined): string {
  return edition?.match(/\d{4}/)?.[0] ?? "";
}

export function buildLegacyPublicationLookup(
  publications: LegacyPublication[],
): Map<string, LegacyPublication> {
  const map = new Map<string, LegacyPublication>();
  for (const pub of publications) {
    map.set(String(pub.id), pub);
  }
  return map;
}

export function resolveStoredPublicationLabel(
  storedEdition: string | null | undefined,
  lookup: Map<string, LegacyPublication>,
  newAxisLookup?: Map<string, NewAxisEdition>,
): string {
  if (!storedEdition) return "—";
  if (isStoredEditionUuid(storedEdition)) {
    return newAxisLookup?.get(storedEdition.toLowerCase())?.label ?? storedEdition;
  }
  const pub = lookup.get(storedEdition);
  if (pub) return formatLegacyPublicationLabel(pub);
  return storedEdition;
}
